/**
 * ECharts 的薄包装（KTD7），取代旧版 TrendChart / PieChart 里各自的初始化代码。
 * 挂载时初始化，option 变化时只 setOption，容器尺寸变化时 resize，卸载时 dispose；
 * 主题切换时销毁实例，用新主题重新初始化（ECharts 不支持运行时换主题）。
 */
import { useEffect, useMemo, useRef } from 'react'
import { theme } from 'antd'
import * as echarts from 'echarts/core'
import { LineChart, PieChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import type { EChartsCoreOption } from 'echarts/core'
import { useAppliedTheme } from '@/stores/theme'
import { buildChartTheme } from '@/theme/chartTheme'

// 按需注册，只包含页面用到的图表和组件
echarts.use([LineChart, PieChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

interface EChartProps {
  option: EChartsCoreOption
  height?: number
  className?: string
  ariaLabel?: string
}

export function EChart({ option, height = 300, className, ariaLabel }: Readonly<EChartProps>) {
  const containerRef = useRef<HTMLElement>(null)
  const chartRef = useRef<echarts.ECharts | null>(null)
  // 初始化时要用最新的 option，但 option 变化本身不触发重建
  const optionRef = useRef(option)
  const appliedTheme = useAppliedTheme()
  const { token } = theme.useToken()
  const chartTheme = useMemo(() => buildChartTheme(appliedTheme, token), [appliedTheme, token])

  useEffect(() => {
    const container = containerRef.current!
    const chart = echarts.init(container, chartTheme)
    chart.setOption(optionRef.current)
    chartRef.current = chart
    const resizeObserver = new ResizeObserver(() => chart.resize())
    resizeObserver.observe(container)
    return () => {
      resizeObserver.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [chartTheme])

  useEffect(() => {
    if (optionRef.current === option) return
    optionRef.current = option
    // 不合并旧配置：系列数量变少时不留下旧系列
    chartRef.current?.setOption(option, { notMerge: true })
  }, [option])

  // 用原生 figure 承载图表画布，aria-label 给读屏软件说明图表内容
  return (
    <figure
      ref={containerRef}
      className={className}
      style={{ width: '100%', height, margin: 0 }}
      aria-label={ariaLabel}
      data-theme={appliedTheme}
    />
  )
}
